<script lang="ts">
  import { Check, Monitor, Moon, Pipette, RotateCcw, Sun } from '@lucide/svelte';
  import { fa } from '../shared/i18n/fa';
  import { cn } from '../shared/lib/cn';
  import { ACCENT_SEED, ACCENTS, theme, type Accent, type ThemeMode } from '../shared/theme/theme.svelte';
  import { buildTones, HEX_COLOR } from '../shared/theme/tones';
  import { Button, buttonVariants, DetailPanel } from '../shared/ui';

  /**
   * Light or dark and a colour, each the member's own choice (ADR-047, ADR-050, ADR-060). A pick
   * applies at once and leaves nothing unsaved, so the choices are plain buttons with `aria-current`
   * (a radio or `aria-pressed` would keep the panel open on an outside click), and the colour
   * fields keep their events to themselves for the same reason.
   */
  let { open, onClose }: { open: boolean; onClose: () => void } = $props();
  const MODES: { mode: ThemeMode; Icon: typeof Sun }[] = [
    { mode: 'system', Icon: Monitor },
    { mode: 'light', Icon: Sun },
    { mode: 'dark', Icon: Moon },
  ];
  const choice = (selected: boolean) =>
    cn(
      'flex min-h-11 items-center gap-2 rounded-md px-3 text-sm transition-[background-color,box-shadow,color] duration-[var(--dur-fast)]',
      selected ? 'bg-accent-soft font-semibold text-tone-ink shadow-pressed' : 'bg-surface text-text-2 shadow-raised-sm hover:bg-surface-2 hover:text-text',
    );

  const custom = $derived(theme.accent.startsWith('#'));
  let typed = $state<string | null>(null);
  const hexField = $derived(typed ?? theme.seed);
  const pick = (value: string) => {
    const hex = value.trim().toLowerCase();
    if (HEX_COLOR.test(hex)) theme.setAccent(hex as Accent);
  };
  // The native picker reports every step of a drag; the screen follows at most once a frame.
  let frame = 0;
  const pickLive = (value: string) => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => pick(value));
  };
</script>

<DetailPanel {open} {onClose} title={fa.theme.panel} subtitle={fa.theme.personal}>
  <div class="flex flex-col gap-6">
    <section aria-label={fa.theme.previewTitle} class="neu-card flex flex-col gap-3 p-4">
      <div class="flex items-center justify-between gap-3">
        <span class="text-sm font-semibold text-text">{fa.theme.previewCard}</span>
        <span class="h-2 w-16 overflow-hidden rounded-full bg-surface-sunken shadow-sunken" aria-hidden="true">
          <span class="block h-full w-2/3 rounded-full bg-accent-solid"></span>
        </span>
      </div>
      <div class="flex gap-2" aria-hidden="true">
        <span class={buttonVariants({ size: 'sm' })}>{fa.theme.previewAction}</span>
        <span class={buttonVariants({ variant: 'secondary', size: 'sm' })}>{fa.theme.previewSecondary}</span>
      </div>
    </section>

    <section aria-labelledby="appearance-mode" class="flex flex-col gap-2.5">
      <h3 id="appearance-mode" class="text-sm font-semibold text-text-2">{fa.theme.modeTitle}</h3>
      <div class="grid grid-cols-3 gap-2.5">
        {#each MODES as { mode, Icon } (mode)}
          <button
            type="button"
            class={cn(choice(theme.mode === mode), 'flex-col justify-center gap-1 px-1 py-2 text-center')}
            aria-current={theme.mode === mode ? 'true' : undefined}
            onclick={() => theme.set(mode)}><Icon class="size-4 shrink-0" aria-hidden="true" />{fa.theme.modes[mode]}</button
          >
        {/each}
      </div>
    </section>

    <section aria-labelledby="appearance-accent" class="flex flex-col gap-2.5">
      <h3 id="appearance-accent" class="text-sm font-semibold text-text-2">{fa.theme.accentTitle}</h3>
      <div class="grid grid-cols-2 gap-2.5">
        {#each ACCENTS as accent (accent)}
          <button
            type="button"
            class={choice(theme.accent === accent)}
            aria-current={theme.accent === accent ? 'true' : undefined}
            onclick={() => {
              typed = null;
              theme.setAccent(accent);
            }}
            ><span class="relative size-5 shrink-0 rounded-full shadow-raised-sm" style:background-color={ACCENT_SEED[accent]} aria-hidden="true"
              ><span class="absolute -bottom-0.5 -end-0.5 size-2.5 rounded-full border border-surface" style:background-color={buildTones(ACCENT_SEED[accent], 'light')['--tone']}></span></span
            ><span class="flex-1 text-start">{fa.theme.accents[accent]}</span>{#if theme.accent === accent}<Check
                class="size-4 shrink-0 text-accent"
                aria-hidden="true"
              />{/if}</button
          >
        {/each}
      </div>
    </section>

    <section aria-labelledby="appearance-custom" class="flex flex-col gap-2.5">
      <h3 id="appearance-custom" class="text-sm font-semibold text-text-2">{fa.theme.customTitle}</h3>
      <div class="flex items-center gap-2.5">
        <label
          class={cn(
            'relative flex size-11 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md shadow-raised-sm',
            custom && 'outline-2 outline-offset-2 outline-accent',
          )}
          style:background-color={theme.seed}
        >
          <span class="sr-only">{fa.theme.customPicker}</span>
          <Pipette class="size-4 text-on-accent" aria-hidden="true" />
          <input
            type="color"
            value={theme.seed}
            class="absolute inset-0 cursor-pointer opacity-0"
            oninput={(e) => {
              e.stopPropagation();
              typed = null;
              pickLive(e.currentTarget.value);
            }}
            onchange={(e) => e.stopPropagation()}
          />
        </label>
        <input
          aria-label={fa.theme.customTitle}
          dir="ltr"
          spellcheck="false"
          autocomplete="off"
          maxlength="7"
          class="field-focus min-h-11 w-32 rounded-md border border-border bg-surface-sunken px-3 text-sm text-text shadow-sunken"
          value={hexField}
          oninput={(e) => {
            e.stopPropagation();
            typed = e.currentTarget.value;
            pick(typed);
          }}
          onchange={(e) => e.stopPropagation()}
          onblur={() => (typed = null)}
        />
      </div>
      <p class="text-xs text-text-3">{fa.theme.customHint}</p>
    </section>

    <Button
      variant="ghost"
      size="sm"
      class="self-start"
      onclick={() => {
        typed = null;
        theme.reset();
      }}><RotateCcw class="size-3.5" aria-hidden="true" />{fa.theme.reset}</Button
    >
  </div>
</DetailPanel>
